/* ==========================================================================
   product-configurator.js
   ==========================================================================
   All interactive behaviour for sections/product-configurator.liquid.

   Vanilla JS, no framework, no jQuery, no external libraries. Loaded with
   `defer` from the section itself, so it only ever downloads on the
   configurator template.

   THREE RULES THIS FILE FOLLOWS
   ----------------------------
   1. No hardcoded prices or upcharges. Every number on screen is derived
      from the real variant prices in the section's JSON data block.
   2. No custom cart logic. Add to cart is handed to Impulse's own
      `theme.AjaxProduct`, which is what makes the cart drawer open and the
      cart count update exactly as on a normal product page.
   3. No DOM moves. Elements that sit in different places on desktop and
      mobile are re-ordered by CSS, never by JavaScript.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  /**
   * Tag aliases for image alt-text matching, by option position.
   *
   * The brief specifies `[size:sofa][fabric:oat]`. But options here map by
   * position, not name, so a tag is accepted if its key is either one of
   * these aliases OR the handleized real option name — which means
   * `[leather-finish:paloma-fog]` works on the Stressless products without
   * anyone having to rename anything.
   */
  var TAG_ALIASES = [
    ['size', 'step1'],
    ['fabric', 'colour', 'color', 'leather', 'material', 'cover', 'step2'],
    ['leg', 'legs', 'leg-finish', 'base', 'finish', 'wood', 'step3']
  ];

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Formats integer cents using the theme's own money helper when it is
   * available, so the configurator matches the rest of the site exactly.
   * Whole-dollar amounts lose their decimals, per the spec.
   */
  function formatMoney(cents, moneyFormat) {
    var out;

    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      out = theme.Currency.formatMoney(cents, moneyFormat);
    } else {
      // Fallback only — Impulse normally exposes theme.Currency.
      out = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      }).format(cents / 100);
    }

    // "No decimals for whole-dollar prices." Handles both . and , separators
    // and only strips a trailing zero-decimal group, never real cents.
    if (cents % 100 === 0) {
      out = out.replace(/([.,])00(?!\d)/, '');
    }

    return out;
  }

  /** Splits alt text into lowercase word tokens for phrase matching. */
  function tokenize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/\[[^\]]*\]/g, ' ') // drop bracket tags, they are matched separately
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean);
  }

  /** True when `phrase` appears in `tokens` as a consecutive run of words. */
  function tokensContainPhrase(tokens, phrase) {
    var needle = tokenize(phrase);
    if (!needle.length) return false;

    for (var i = 0; i + needle.length <= tokens.length; i++) {
      var hit = true;
      for (var j = 0; j < needle.length; j++) {
        if (tokens[i + j] !== needle[j]) {
          hit = false;
          break;
        }
      }
      if (hit) return true;
    }
    return false;
  }

  /** Parses `[size:sofa][fabric:oat]` into { size: 'sofa', fabric: 'oat' }. */
  function parseTags(alt) {
    var tags = {};
    var re = /\[([a-z0-9_-]+)\s*:\s*([^\]]+)\]/gi;
    var match;

    while ((match = re.exec(String(alt || ''))) !== null) {
      tags[handleize(match[1])] = handleize(match[2]);
    }
    return tags;
  }

  /**
   * True when an option value is real, rather than one of Shopify's
   * placeholders for "this product has no options".
   */
  function meaningful(value) {
    return !!value && value !== 'Default Title' && value !== 'Title';
  }

  /** Strips bracket tags so a screen reader never reads the lookup keys. */
  function cleanAlt(alt) {
    return String(alt || '').replace(/\[[^\]]*\]/g, '').trim();
  }

  /* ------------------------------------------------------------------ *
   * Configurator
   * ------------------------------------------------------------------ */

  function Configurator(root) {
    this.root = root;

    var dataEl = root.querySelector('[data-configurator-data]');
    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      // A malformed data block must not take the whole product page down.
      console.warn('[configurator] could not parse data block', e);
      return;
    }

    // The media array ends with a trailing `null` so the Liquid loop did not
    // need forloop.last logic inside a filtered loop. Drop it here.
    this.media = (this.data.media || []).filter(Boolean).map(function (m) {
      m.tags = parseTags(m.alt);
      m.tokens = tokenize(m.alt);
      m.cleanAlt = cleanAlt(m.alt);
      return m;
    });

    this.variants = this.data.variants || [];
    this.optionCount = this.data.optionCount || 1;
    this.strings = this.data.strings || {};
    this.maxThumbs = this.data.maxThumbs || 0; // 0 means "no limit"

    this.stock = this.data.stock || {};
    this.stockMode = 'all';
    this.collectOptionValues();
    this.buildMediaGroups();

    this.cacheDom();
    this.initState();
    this.bind();

    // First render fills in everything Liquid deliberately left empty
    // (per-value prices, availability, build rows).
    this.render({ initial: true });
    this.initStockMode();

    this.initSwatchBox();
    this.initStickyBar();
    this.initThumbArrows();
    this.initStageSwipe();
    this.initAddToCart();
    this.initPortedBlocks();
  }

  Configurator.prototype.cacheDom = function () {
    var root = this.root;

    this.dom = {
      form: root.querySelector('[data-pcfg-form]'),
      variantIdInput: root.querySelector('[data-pcfg-variant-id]'),
      atc: root.querySelector('[data-pcfg-atc]'),
      atcText: root.querySelector('[data-pcfg-atc-text]'),
      atcSub: root.querySelector('[data-pcfg-atc-sub]'),
      stockBand: root.querySelector('[data-pcfg-stock-band]'),
      stockTitle: root.querySelector('[data-pcfg-stock-title]'),
      stockNote: root.querySelector('[data-pcfg-stock-note]'),
      stockHint: root.querySelector('[data-pcfg-stock-hint]'),
      stockFilter: root.querySelector('[data-pcfg-stock-filter]'),
      price: root.querySelector('[data-pcfg-price]'),
      compare: root.querySelector('[data-pcfg-compare]'),
      savings: root.querySelector('[data-pcfg-savings]'),
      savingsLine: root.querySelector('[data-pcfg-savings-line]'),
      announce: root.querySelector('[data-pcfg-announce]'),

      mainImage: root.querySelector('[data-pcfg-main-image]'),
      stage: root.querySelector('.pcfg__stage'),
      caption: root.querySelector('[data-pcfg-caption]'),
      captionLeft: root.querySelector('[data-pcfg-caption-left]'),
      captionRight: root.querySelector('[data-pcfg-caption-right]'),
      thumbs: Array.prototype.slice.call(root.querySelectorAll('[data-pcfg-thumb]')),
      thumbStrip: root.querySelector('[data-pcfg-thumbs]'),
      thumbPrev: root.querySelector('[data-pcfg-thumbs-prev]'),
      thumbNext: root.querySelector('[data-pcfg-thumbs-next]'),

      steps: Array.prototype.slice.call(root.querySelectorAll('[data-pcfg-step]')),
      optionButtons: Array.prototype.slice.call(root.querySelectorAll('[data-pcfg-option]')),

      buildRows: Array.prototype.slice.call(root.querySelectorAll('[data-pcfg-build-row]')),
      buildTotal: root.querySelector('[data-pcfg-build-total]'),

      sticky: root.querySelector('[data-pcfg-sticky]'),
      stickyPrice: root.querySelector('[data-pcfg-sticky-price]'),
      stickyAtc: root.querySelector('[data-pcfg-sticky-atc]'),

      appointment: root.querySelector('[data-pcfg-appointment]'),
      swatchBox: root.querySelector('[data-pcfg-swatchbox]')
    };
  };

  /**
   * Initial state comes from ?variant= when present, so every build is a
   * shareable link that restores exactly. Otherwise it comes from the
   * variant Liquid already rendered.
   */
  Configurator.prototype.initState = function () {
    var params = new URLSearchParams(window.location.search);
    var fromUrl = parseInt(params.get('variant'), 10);
    var variant =
      (fromUrl && this.variantById(fromUrl)) ||
      this.variantById(this.data.initialVariantId) ||
      this.variants.filter(function (v) { return v.available; })[0] ||
      this.variants[0];

    this.state = { values: [] };

    for (var i = 0; i < this.optionCount; i++) {
      this.state.values[i] = variant ? variant.options[i] : null;
    }

    this.currentVariant = variant || null;
  };

  /**
   * The distinct values of each option, in first-seen order, derived from
   * the variants. Needed so an image's alt text can be matched against
   * every possible value without the section having to emit the option
   * lists a second time.
   */
  Configurator.prototype.collectOptionValues = function () {
    this.optionValues = [];

    for (var i = 0; i < this.optionCount; i++) {
      var seen = [];

      for (var v = 0; v < this.variants.length; v++) {
        var value = this.variants[v].options[i];
        if (value && seen.indexOf(value) === -1) seen.push(value);
      }

      this.optionValues.push(seen);
    }
  };

  /**
   * Works out which options each image depicts, then groups the gallery.
   *
   * This product's images are uploaded in runs of four — profile, side,
   * back-side, back — and only the FIRST of each run has alt text naming
   * the options ("…Small Paloma Chocolate Brown Stain"); the other three
   * carry generic alt text or none at all. So an image that names at least
   * one option value STARTS a group, and every following image that names
   * nothing is taken to belong to that same group.
   *
   * That turns "show the photos for this build" into a contiguous slice,
   * with no naming convention for Ryan to maintain beyond what he already
   * does when uploading.
   */
  Configurator.prototype.buildMediaGroups = function () {
    this.groups = [];

    var current = null;

    for (var m = 0; m < this.media.length; m++) {
      var media = this.media[m];
      var signature = this.signatureFor(media);

      if (signature || !current) {
        current = { signature: signature || [], media: [] };
        this.groups.push(current);
      }

      media.group = current;
      current.media.push(media);
    }
  };

  /**
   * The option values an image names, as an array by option index, or null
   * when it names none. Bracket tags win over plain alt text.
   */
  Configurator.prototype.signatureFor = function (media) {
    var signature = [];
    var names = this.data.optionNames || [];
    var found = false;

    for (var i = 0; i < this.optionCount; i++) {
      signature[i] = null;

      var keys = TAG_ALIASES[i] ? TAG_ALIASES[i].slice() : [];
      if (names[i]) keys.push(handleize(names[i]));

      for (var k = 0; k < keys.length; k++) {
        if (Object.prototype.hasOwnProperty.call(media.tags, keys[k])) {
          signature[i] = media.tags[keys[k]];
          found = true;
          break;
        }
      }

      if (signature[i]) continue;

      var values = this.optionValues[i] || [];
      for (var v = 0; v < values.length; v++) {
        if (tokensContainPhrase(media.tokens, values[v])) {
          signature[i] = handleize(values[v]);
          found = true;
          break;
        }
      }
    }

    return found ? signature : null;
  };

  /**
   * The image group that best depicts `values`. The option nominated in the
   * theme editor (usually the fabric or leather) is weighted heaviest,
   * because a wrong-size photo of the right leather is a far better answer
   * than a right-size photo of the wrong one.
   */
  Configurator.prototype.bestGroup = function (values) {
    var primary = this.data.imagePrimaryIndex;
    var best = null;
    var bestScore = -1;

    for (var g = 0; g < this.groups.length; g++) {
      var group = this.groups[g];
      var score = 0;

      for (var i = 0; i < this.optionCount; i++) {
        if (!values[i] || !group.signature[i]) continue;
        if (group.signature[i] === handleize(values[i])) {
          score += (i === primary ? 3 : 1);
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = group;
      }
    }

    return bestScore > 0 ? best : null;
  };

  Configurator.prototype.variantById = function (id) {
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].id === id) return this.variants[i];
    }
    return null;
  };

  /* ------------------------------------------------------------------ *
   * Variant matching
   * ------------------------------------------------------------------ */

  /** Exact match on every option. Variant combinations are unique. */
  Configurator.prototype.exactMatch = function (values) {
    var count = this.optionCount;
    return this.variants.filter(function (v) {
      for (var i = 0; i < count; i++) {
        if (v.options[i] !== values[i]) return false;
      }
      return true;
    })[0] || null;
  };

  /**
   * Resolves a click to a variant without ever dead-ending the shopper.
   *
   * On a sparse matrix — the Stressless test product has 19 of a possible
   * 30 combinations — insisting on an exact match means a click can land on
   * nothing at all. Instead we honour the option the shopper just touched,
   * then give up the *later* steps first, then any step, and only return an
   * unavailable variant when the store genuinely has nothing to sell.
   */
  Configurator.prototype.resolve = function (values, changedIndex) {
    var count = this.optionCount;
    var self = this;

    var exact = this.exactMatch(values);
    if (exact && exact.available) return exact;

    function score(v) {
      var n = 0;
      for (var i = 0; i < count; i++) {
        if (v.options[i] === values[i]) n++;
      }
      return n;
    }

    // Keep everything up to and including the changed step, relax the rest.
    var candidates = this.variants.filter(function (v) {
      if (!v.available) return false;
      for (var i = 0; i <= changedIndex; i++) {
        if (v.options[i] !== values[i]) return false;
      }
      return true;
    });

    // Failing that, keep only the step that was actually clicked.
    if (!candidates.length) {
      candidates = this.variants.filter(function (v) {
        return v.available && v.options[changedIndex] === values[changedIndex];
      });
    }

    if (candidates.length) {
      candidates.sort(function (a, b) {
        var diff = score(b) - score(a);
        return diff !== 0 ? diff : a.price - b.price;
      });
      return candidates[0];
    }

    // Nothing available at all — return an unavailable variant so the page
    // can show the shopper what they picked and disable Add to cart.
    if (exact) return exact;

    return this.variants.filter(function (v) {
      return v.options[changedIndex] === values[changedIndex];
    })[0] || self.currentVariant;
  };

  /**
   * Lowest price across every variant matching `values` for options
   * 0..depth-1, ignoring options at or beyond `depth`. Returns null when
   * nothing matches. This one function drives every derived price on the
   * page (see configurator-build-summary.liquid for the arithmetic).
   */
  Configurator.prototype.prefixMin = function (values, depth) {
    var min = null;

    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;

      for (var j = 0; j < depth; j++) {
        if (v.options[j] !== values[j]) { ok = false; break; }
      }

      if (ok && (min === null || v.price < min)) min = v.price;
    }

    return min;
  };

  /** True when some available variant exists with `value` at `index`, given
   *  the shopper's current choices in every OTHER step. */
  Configurator.prototype.isCombinationAvailable = function (index, value) {
    var values = this.state.values;
    var count = this.optionCount;

    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (!v.available) continue;
      if (v.options[index] !== value) continue;

      var ok = true;
      for (var j = 0; j < count; j++) {
        if (j !== index && v.options[j] !== values[j]) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  };

  /* ------------------------------------------------------------------ *
   * Image matching
   * ------------------------------------------------------------------ */

  /**
   * Picks the best product image for the current build, in this order:
   *   1. bracket-tagged media, best score wins (size+fabric beats fabric)
   *   2. plain alt text containing the option values — which is what the
   *      Stressless products already have ("...Small Paloma Chocolate
   *      Brown Stain"), so image swapping works with no content prep
   *   3. the variant's own featured image
   *   4. the product's featured image
   */
  /**
   * The image to show for the current build: the lead photo of the best
   * matching group, falling back to the variant's own featured image and
   * then the product's.
   */
  Configurator.prototype.findImage = function (variant) {
    // Setting "Main photo for a build" = variant (default): the image assigned to the variant in the
    // admin wins. Alt-text / tag matching below is the fallback for variants without one.
    if ((this.data.imageSource || 'variant') === 'variant' && variant && variant.featuredImage) {
      return { src: variant.featuredImage, alt: this.data.productTitle, mediaId: variant.featuredMediaId };
    }

    var group = this.bestGroup(this.state.values);

    if (group && group.media.length) {
      return { src: group.media[0].src, alt: group.media[0].cleanAlt, mediaId: group.media[0].id };
    }

    if (variant && variant.featuredImage) return { src: variant.featuredImage, alt: this.data.productTitle };
    if (this.data.featuredImage) return { src: this.data.featuredImage, alt: this.data.productTitle };
    return null;
  };

  /**
   * Swaps the main image with a short crossfade.
   *
   * The `srcset` attribute is REMOVED before the swap. Leaving it in place
   * lets the browser keep serving a candidate from the old set, and the
   * image visibly does not change — this has bitten this theme before.
   */
  Configurator.prototype.setMainImage = function (target) {
    var img = this.dom.mainImage;
    if (!img || !target || !target.src) return;

    // A still photo is taking the stage — clear any inline video first so the
    // option changes (and image-tile clicks) always return to a photo.
    this.hideStageVideo();

    // Take the token BEFORE the same-src shortcut: going A → B → A quickly must
    // cancel B's still-loading swap, or B lands last and the stage shows the
    // wrong photo while the A thumbnail is highlighted.
    var token = (this._imageToken = (this._imageToken || 0) + 1);
    if (img.getAttribute('src') === target.src) {
      img.classList.remove('is-swapping');
      return;
    }

    /* Each swap takes a token. A shopper clicking quickly through leathers
       can have several preloads in flight at once, and they do not finish
       in the order they started — a large first-time CDN fetch can land
       after a later, already-cached one. Without this guard the stale
       image wins and the stage shows the wrong build. Only the most recent
       swap is allowed to touch the <img>. */
    var self = this;
    var preload = new Image();

    function apply() {
      if (token !== self._imageToken) return;

      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = target.src;
      if (target.alt) img.alt = target.alt;
      img.classList.remove('is-swapping');
    }

    preload.onload = function () {
      if (token !== self._imageToken) return;

      img.classList.add('is-swapping');
      window.setTimeout(apply, 120);
    };

    // If the preload fails, swap anyway rather than leaving a stale image.
    preload.onerror = apply;

    preload.src = target.src;
  };

  /* ------------------------------------------------------------------ *
   * Selection
   * ------------------------------------------------------------------ */

  Configurator.prototype.bind = function () {
    var self = this;

    this.dom.optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var index = parseInt(btn.getAttribute('data-option-index'), 10);
        self.select(index, btn.getAttribute('data-value'));
      });

      // Roving arrow-key navigation within a step.
      btn.addEventListener('keydown', function (evt) {
        var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (keys.indexOf(evt.key) === -1) return;

        var group = btn.closest('[data-pcfg-values]');
        if (!group) return;

        var siblings = Array.prototype.slice.call(group.querySelectorAll('[data-pcfg-option]'));
        var here = siblings.indexOf(btn);
        var step = (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') ? 1 : -1;
        var next = siblings[(here + step + siblings.length) % siblings.length];

        if (next) {
          evt.preventDefault();
          next.focus();
        }
      });
    });

    /* Preload a tile's FULL-size photo (the exact width:1600 url the stage
       will load) the instant a pointer touches it, so by the time the click
       lands the image is already cached and the crossfade in setMainImage
       runs immediately instead of waiting on a first-time CDN fetch — which
       is the lag you feel on the first view of each photo. */
    function preloadThumb(thumb) {
      var src = thumb.getAttribute('data-thumb-src');
      if (!src || thumb._pcfgWarmed) return;
      thumb._pcfgWarmed = true;
      var im = new Image();
      im.decoding = 'async';
      im.src = src;
    }

    this.dom.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        self.activateThumb(thumb);
      });
      thumb.addEventListener('mouseenter', function () { preloadThumb(thumb); });
      thumb.addEventListener('focus', function () { preloadThumb(thumb); });
      thumb.addEventListener('touchstart', function () { preloadThumb(thumb); }, { passive: true });
    });

    /* Belt-and-braces for touch and un-hovered clicks: once the page is idle,
       warm every tile's full photo in the background, one at a time so it
       never competes with anything the shopper actually triggers. Skipped on
       data-saver connections. */
    (function warmAll() {
      try { if (navigator.connection && navigator.connection.saveData) return; } catch (e) {}
      var list = self.dom.thumbs.filter(function (t) { return t.getAttribute('data-thumb-src'); });
      var i = 0;
      var schedule = window.requestIdleCallback || function (fn) { return window.setTimeout(fn, 400); };
      function next() {
        if (i >= list.length) return;
        preloadThumb(list[i++]);
        schedule(next);
      }
      schedule(next);
    })();

    if (this.dom.stockFilter) {
      this.dom.stockFilter.addEventListener('click', function (evt) {
        var b = evt.target.closest('[data-pcfg-stock-mode]');
        if (b) self.setStockMode(b.getAttribute('data-pcfg-stock-mode'));
      });
    }
    if (this.dom.stockHint) {
      this.dom.stockHint.addEventListener('click', function () {
        self.applyVariant(self.variantById(parseInt(self.dom.stockHint.getAttribute('data-variant-id'), 10)));
      });
    }
  };

  Configurator.prototype.select = function (index, value) {
    if (isNaN(index) || value === null) return;
    if (this.state.values[index] === value) return;

    var desired = this.state.values.slice();
    desired[index] = value;

    var variant = this.resolve(desired, index);
    if (!variant) return;

    for (var i = 0; i < this.optionCount; i++) {
      this.state.values[i] = variant.options[i];
    }

    this.currentVariant = variant;
    this.render({});
  };

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  Configurator.prototype.render = function (opts) {
    opts = opts || {};

    var variant = this.currentVariant;
    var fmt = this.data.moneyFormat;
    var values = this.state.values;

    /* ---- Price -------------------------------------------------- */
    var priceText = variant ? formatMoney(variant.price, fmt) : '';

    if (this.dom.price) this.dom.price.textContent = priceText;
    if (this.dom.stickyPrice) this.dom.stickyPrice.textContent = priceText;

    this.renderSalePrice(variant);

    /* ---- Add to cart -------------------------------------------- */
    if (this.dom.atc && this.dom.atcText) {
      var sellable = !!(variant && variant.available);

      this.dom.atc.disabled = !sellable;
      // atcShowPrice === false hides the price on the button (set during a DN promo,
      // since the button shows the pre-discount price). Undefined/true keeps the price.
      var atcLabel = this.strings.atcShowPrice === false
        ? this.strings.atcPrefix
        : this.strings.atcPrefix + ' — ' + priceText;
      this.dom.atcText.textContent = sellable
        ? atcLabel
        : (variant ? this.strings.unavailable : this.strings.soldOut);
    }

    if (this.dom.stickyAtc) {
      this.dom.stickyAtc.disabled = !(variant && variant.available);
    }

    /* ---- Hidden form input -------------------------------------- */
    if (this.dom.variantIdInput && variant) {
      this.dom.variantIdInput.value = variant.id;
    }

    /* ---- Shareable URL ------------------------------------------ */
    // replaceState, not pushState: a shopper trying six fabrics should not
    // have to press Back six times to leave the page.
    if (variant && !opts.initial && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* ---- Option buttons ----------------------------------------- */
    this.renderOptionButtons();
    this.renderStock();
    this.renderSpecs();

    /* ---- Step headers ------------------------------------------- */
    this.dom.steps.forEach(function (step) {
      var index = parseInt(step.getAttribute('data-option-index'), 10);
      var current = step.querySelector('[data-pcfg-step-current]');
      if (current) current.textContent = values[index] || '';
    });

    /* ---- Caption bar -------------------------------------------- */
    /* Shopify names the sole option of a single-variant product "Default
       Title". Writing that into the caption puts a meaningless label — and
       the gradient behind it — across the bottom of the image, so those
       placeholder values are filtered out here exactly as they are in the
       Liquid, and the whole bar hides when nothing is left. */
    var leftParts = [];
    if (meaningful(values[0])) leftParts.push(values[0]);
    if (variant && variant.dimensions) leftParts.push(variant.dimensions);

    var rightParts = [];
    if (meaningful(values[1])) rightParts.push(values[1]);
    if (meaningful(values[2])) rightParts.push(values[2]);

    if (this.dom.captionLeft) this.dom.captionLeft.textContent = leftParts.join(' · ');
    if (this.dom.captionRight) this.dom.captionRight.textContent = rightParts.join(' · ');

    if (this.dom.caption) {
      this.dom.caption.hidden = !(leftParts.length || rightParts.length);
    }

    /* ---- Main image and thumbnails ------------------------------ */
    // Run on the initial render too, so a shared ?variant= link that points
    // at a build whose photo is not the product's featured image still
    // opens on the right photo. When the two agree — the common case —
    // setMainImage early-returns on the identical src and the server's
    // srcset-optimised LCP image is left untouched.
    // First paint may lead with a lifestyle photo chosen server-side (see LEAD IMAGE in
    // product-configurator.liquid); after that, option changes drive the image as usual.
    var target = (opts.initial && this.data.leadImage)
      ? { src: this.data.leadImage, alt: this.data.leadAlt || this.data.productTitle, mediaId: this.data.leadMediaId }
      : this.findImage(variant);
    this.setMainImage(target);
    this.markActiveThumb(target && target.mediaId);
    this.renderThumbs();

    /* ---- Your build --------------------------------------------- */
    this.renderBuild();

    /* ---- Appointment link --------------------------------------- */
    if (this.dom.appointment && variant) {
      var base = this.dom.appointment.getAttribute('data-base-href') || '';
      if (base) {
        var joiner = base.indexOf('?') === -1 ? '?' : '&';
        this.dom.appointment.href = base + joiner + 'variant=' + variant.id;
      }
    }

    /* ---- Swatch box label --------------------------------------- */
    this.renderSwatchBox();

    /* ---- Ported blocks that track the variant -------------------- */
    this.renderSku();

    /* ---- Announce ----------------------------------------------- */
    if (!opts.initial) this.announce(priceText);

    /* ---- Let the rest of the theme know ------------------------- */
    // Impulse blocks such as the Storis SKU listen for these, so keeping
    // them in sync costs nothing and avoids surprises.
    if (variant) {
      var detail = { variant: variant, currentlySelectedValues: values.slice() };
      this.root.dispatchEvent(new CustomEvent('variantChange', { detail: detail, bubbles: true }));
      document.dispatchEvent(new CustomEvent('variant:change', { detail: detail }));
    }
  };

  /**
   * Compare-at price and the saving.
   *
   * Driven per variant, not per product: on this catalog Small and Large
   * frequently carry different compare-at values, so the saving has to be
   * recomputed on every build change rather than rendered once by Liquid.
   *
   * Everything hides when the variant is not actually discounted — a
   * struck-through price equal to the real one is worse than none.
   */
  /* ------------------------------------------------------------------ *
   * Stock status: status band, option dots, "In stock now" switch and
   * the Add to cart sub-line — all from one reading of each variant.
   * ------------------------------------------------------------------ */

  /** 'ok' (in stock), 'so' (special order) or null (can't be bought). */
  Configurator.prototype.statusOf = function (v) {
    if (!v || !v.available) return null;
    var st = v.status || '';
    if (st === 'in_stock') return 'ok';
    if (st === 'special_order' || st === 'on_display') return 'so';
    // No status set: stock on hand = in stock; selling past zero = special order.
    return v.qty > 0 ? 'ok' : 'so';
  };

  Configurator.prototype.stockLine = function (v) {
    var k = this.stock;
    var st = this.statusOf(v);
    if (!st) return '';
    var weeks;
    if (st === 'ok') {
      weeks = k.okWeeks;
    } else {
      var min = v.minW != null ? v.minW : k.soMin;
      var max = v.maxW != null ? v.maxW : k.soMax;
      weeks = (min === max ? min : min + '–' + max) + ' ' + k.weeksWord;
    }
    // An empty delivery wording setting (in stock / special order are separate) leaves the wording out entirely (no fallback word).
    var when = [st === 'ok' ? k.delivered : k.soDelivered, weeks].filter(function (x) { return x && String(x).trim(); }).join(' ');
    return (st === 'ok' ? k.okLabel : k.soLabel) + (when ? ' · ' + when : '');
  };

  /** Status of the exact build the shopper gets by picking `value` here. */
  Configurator.prototype.probeStatus = function (index, value) {
    var values = this.state.values.slice();
    values[index] = value;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var j = 0; j < this.optionCount; j++) {
        if (v.options[j] !== values[j]) { ok = false; break; }
      }
      if (ok) return this.statusOf(v);
    }
    return null;
  };

  /** Closest in-stock build to the current one; earlier steps weigh more. */
  Configurator.prototype.nearestInStock = function () {
    var values = this.state.values;
    var best = null;
    var bestScore = -1;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (this.statusOf(v) !== 'ok') continue;
      var score = 0;
      for (var j = 0; j < this.optionCount; j++) {
        if (v.options[j] === values[j]) score += Math.pow(2, this.optionCount - j);
      }
      if (score > bestScore) { best = v; bestScore = score; }
    }
    return best;
  };

  Configurator.prototype.hasStockMix = function () {
    if (this._mix == null) {
      var ok = false, so = false, self = this;
      this.variants.forEach(function (v) {
        var st = self.statusOf(v);
        if (st === 'ok') ok = true;
        if (st === 'so') so = true;
      });
      this._mix = ok && so;
    }
    return this._mix;
  };

  Configurator.prototype.applyVariant = function (v) {
    if (!v) return;
    for (var i = 0; i < this.optionCount; i++) this.state.values[i] = v.options[i];
    this.currentVariant = v;
    this.render({});
  };

  Configurator.prototype.setStockMode = function (mode) {
    this.stockMode = mode === 'ok' ? 'ok' : 'all';
    var self = this;
    this.dom.stockFilter.querySelectorAll('[data-pcfg-stock-mode]').forEach(function (b) {
      var on = b.getAttribute('data-pcfg-stock-mode') === self.stockMode;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // Switching to "In stock now" on a special-order build moves to the nearest ready one.
    if (this.stockMode === 'ok' && this.statusOf(this.currentVariant) !== 'ok') {
      var n = this.nearestInStock();
      if (n) { this.applyVariant(n); return; }
    }
    this.renderStock();
  };

  /** "Start with: In stock now" setting. A shared ?variant= link keeps its build; otherwise open on the nearest in-stock one. */
  Configurator.prototype.initStockMode = function () {
    if (!this.stock.enabled || !this.stock.startOk || !this.dom.stockFilter || !this.hasStockMix()) return;
    if (new URLSearchParams(window.location.search).get('variant')) {
      this.stockMode = 'ok';
      this.dom.stockFilter.querySelectorAll('[data-pcfg-stock-mode]').forEach(function (b) {
        var on = b.getAttribute('data-pcfg-stock-mode') === 'ok';
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.renderStock();
    } else {
      this.setStockMode('ok');
    }
  };

  Configurator.prototype.renderStock = function () {
    var k = this.stock;
    if (!k.enabled) return;
    var self = this;
    var v = this.currentVariant;
    var status = this.statusOf(v);
    var mix = this.hasStockMix();

    if (this.dom.stockFilter) this.dom.stockFilter.hidden = !mix;
    if (!mix) this.stockMode = 'all';

    this.dom.optionButtons.forEach(function (btn) {
      var index = parseInt(btn.getAttribute('data-option-index'), 10);
      var ps = self.probeStatus(index, btn.getAttribute('data-value'));
      var isGrid = !btn.classList.contains('pcfg__value--swatch') && !btn.classList.contains('pcfg__value--card');
      var want = mix && ps && (k.dots === 'all' || (k.dots === 'swatch' && !isGrid));
      var dot = btn.querySelector(':scope > .pcfg__stockdot');
      if (want) {
        if (!dot) {
          dot = document.createElement('i');
          dot.className = 'pcfg__stockdot';
          dot.setAttribute('aria-hidden', 'true');
          btn.appendChild(dot);
        }
        dot.classList.toggle('is-ok', ps === 'ok');
        dot.classList.toggle('is-so', ps === 'so');
      } else if (dot) {
        dot.parentNode.removeChild(dot);
      }
      btn.classList.toggle('is-stock-dim', self.stockMode === 'ok' && ps !== 'ok');
      btn.classList.toggle('is-stock-ok', self.stockMode === 'ok' && ps === 'ok');

      // Screen readers get the status the dot shows.
      var label = btn.getAttribute('aria-label');
      if (label !== null) {
        if (btn.__pcfgLabel == null) btn.__pcfgLabel = label;
        btn.setAttribute('aria-label', want ? btn.__pcfgLabel + ', ' + (ps === 'ok' ? k.okLabel : k.soLabel) : btn.__pcfgLabel);
      }
    });

    var line = this.stockLine(v);

    if (this.dom.stockBand) {
      var band = this.dom.stockBand;
      band.hidden = !status;
      band.classList.toggle('is-ok', status === 'ok');
      band.classList.toggle('is-so', status === 'so');
      this.dom.stockTitle.textContent = line;
      var note = status === 'ok' ? k.okNote : k.soNote;
      this.dom.stockNote.textContent = note || '';
      this.dom.stockNote.hidden = !note;

      var hint = this.dom.stockHint;
      var n = status === 'so' && k.hint ? this.nearestInStock() : null;
      if (n) {
        var diff = [];
        for (var i = 0; i < this.optionCount; i++) {
          if (n.options[i] && n.options[i] !== this.state.values[i]) diff.push(n.options[i]);
        }
        hint.textContent = k.hintPrefix + ' ' + diff.join(' · ');
        hint.setAttribute('data-variant-id', n.id);
      }
      hint.hidden = !n;
    }

    if (this.dom.atcSub) {
      this.dom.atcSub.textContent = line;
      this.dom.atcSub.hidden = !(k.atcLine && status);
    }
  };

  /** Spec card: variant dimensions / seat height and the selected-option rows. */
  Configurator.prototype.renderSpecs = function () {
    var self = this;
    var v = this.currentVariant;
    if (this.specMap === undefined) {
      var el = this.root.querySelector('[data-pcfg-spec-map]');
      try { this.specMap = el ? JSON.parse(el.textContent) : null; } catch (e) { this.specMap = null; }
    }
    var row = v && this.specMap ? this.specMap[String(v.id)] : null;
    if (row) {
      var d = this.root.querySelector('[data-pcfg-spec-dims]');
      if (d && row.dims) d.textContent = row.dims;
      var st = this.root.querySelector('[data-pcfg-spec-seat]');
      if (st && row.seat) st.textContent = row.seat;
    }
    this.root.querySelectorAll('[data-pcfg-spec-opt]').forEach(function (span) {
      var ix = parseInt(span.getAttribute('data-pcfg-spec-opt'), 10);
      var val = self.state.values[ix];
      if (val) span.textContent = val;
      var sw = self.root.querySelector('[data-pcfg-spec-swatch="' + ix + '"]');
      if (!sw) return;
      var btn = self.dom.optionButtons.filter(function (b) {
        return parseInt(b.getAttribute('data-option-index'), 10) === ix && b.getAttribute('data-value') === val;
      })[0];
      var img = btn && btn.getAttribute('data-swatch-image');
      var col = btn && btn.getAttribute('data-swatch-color');
      sw.style.backgroundImage = img ? 'url(' + img + ')' : '';
      sw.style.backgroundColor = col || '';
      var zb = sw.closest('[data-pcfg-spec-zoom]');
      if (zb) {
        zb.hidden = !(img || col);
        zb.setAttribute('data-swatch-image', img || '');
        zb.setAttribute('data-swatch-color', col || '');
        zb.setAttribute('data-swatch-zoom', (btn && btn.getAttribute('data-swatch-zoom')) || img || '');
        zb.setAttribute('data-zoom-name', val || '');
        zb.setAttribute('aria-label', 'View ' + (val || '') + ' close-up');
      } else {
        sw.hidden = !(img || col);
      }
    });
  };

  /* ------------------------------------------------------------------ *
   * Swatch close-up: tapping a swatch dot in the specs card opens a large view.
   * One popup per page, created on first use.
   * ------------------------------------------------------------------ */
  function openSwatchZoom(btn) {
    var pop = document.getElementById('pcfg-swatch-zoom');
    if (!pop) {
      pop = document.createElement('div');
      pop.id = 'pcfg-swatch-zoom';
      pop.className = 'pcfg-zoom';
      pop.setAttribute('role', 'dialog');
      pop.setAttribute('aria-modal', 'true');
      pop.hidden = true;
      pop.innerHTML =
        '<div class="pcfg-zoom__card">' +
          '<button type="button" class="pcfg-zoom__close" aria-label="Close" data-pcfg-zoom-close>&times;</button>' +
          '<div class="pcfg-zoom__img" data-pcfg-zoom-img></div>' +
          '<p class="pcfg-zoom__cap"><span class="pcfg-zoom__label" data-pcfg-zoom-label></span><span class="pcfg-zoom__name" data-pcfg-zoom-name></span></p>' +
        '</div>';
      document.body.appendChild(pop);
      pop.addEventListener('click', function (e) {
        if (e.target === pop || e.target.closest('[data-pcfg-zoom-close]')) closeSwatchZoom();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !pop.hidden) closeSwatchZoom();
      });
    }
    var src = btn.getAttribute('data-swatch-zoom') || btn.getAttribute('data-swatch-image') || '';
    var col = btn.getAttribute('data-swatch-color') || '';
    var img = pop.querySelector('[data-pcfg-zoom-img]');
    img.style.backgroundImage = src ? 'url(' + src + ')' : '';
    img.style.backgroundColor = col || '';
    pop.querySelector('[data-pcfg-zoom-label]').textContent = btn.getAttribute('data-zoom-label') || '';
    pop.querySelector('[data-pcfg-zoom-name]').textContent = btn.getAttribute('data-zoom-name') || '';
    pop.setAttribute('aria-label', (btn.getAttribute('data-zoom-name') || '') + ' close-up');
    pop.__return = btn;
    pop.hidden = false;
    document.documentElement.classList.add('pcfg-zoom-open');
    pop.querySelector('[data-pcfg-zoom-close]').focus();
  }
  function closeSwatchZoom() {
    var pop = document.getElementById('pcfg-swatch-zoom');
    if (!pop) return;
    pop.hidden = true;
    document.documentElement.classList.remove('pcfg-zoom-open');
    if (pop.__return) pop.__return.focus();
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-pcfg-zoom]');
    if (b) { e.preventDefault(); openSwatchZoom(b); }
  });

  Configurator.prototype.renderSalePrice = function (variant) {
    var compareEl = this.dom.compare;
    var savingsEl = this.dom.savings;
    var lineEl = this.dom.savingsLine;

    if (!compareEl && !savingsEl && !lineEl) return;

    var fmt = this.data.moneyFormat;
    var compare = variant ? variant.compareAtPrice : null;
    var onSale = !!(variant && compare && compare > variant.price);

    if (this.dom.price) this.dom.price.classList.toggle('pcfg__price--sale', onSale);

    if (!onSale) {
      if (compareEl) compareEl.hidden = true;
      if (savingsEl) savingsEl.hidden = true;
      if (lineEl) lineEl.hidden = true;
      return;
    }

    var saved = compare - variant.price;
    var percent = Math.round((saved / compare) * 100);

    if (compareEl) {
      compareEl.textContent = formatMoney(compare, fmt);
      compareEl.hidden = false;
    }

    var amountText = formatMoney(saved, fmt);
    var label = this.strings.savingsLabel || 'Save [amount]';
    var format = this.data.savingsFormat || 'amount';

    var savingText;
    if (format === 'percent') {
      savingText = percent + '% off';
    } else if (format === 'both') {
      savingText = label.replace('[amount]', amountText).replace('[percent]', percent + '%') +
        ' (' + percent + '%)';
    } else {
      savingText = label.replace('[amount]', amountText).replace('[percent]', percent + '%');
    }

    if (savingsEl) {
      savingsEl.textContent = savingText;
      savingsEl.hidden = false;
    }

    if (lineEl) {
      lineEl.textContent = 'Was ' + formatMoney(compare, fmt) + ' · you save ' + amountText;
      lineEl.hidden = false;
    }
  };

  Configurator.prototype.renderOptionButtons = function () {
    var self = this;
    var fmt = this.data.moneyFormat;
    var values = this.state.values;

    this.dom.optionButtons.forEach(function (btn) {
      var index = parseInt(btn.getAttribute('data-option-index'), 10);
      var value = btn.getAttribute('data-value');
      var selected = values[index] === value;

      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');

      /* Availability. Unavailable options stay focusable and clickable —
         the shopper can still choose them, and `resolve()` then finds the
         nearest build that actually exists. */
      var available = self.isCombinationAvailable(index, value);
      btn.classList.toggle('is-unavailable', !available);

      if (available) {
        btn.removeAttribute('title');
      } else {
        btn.setAttribute('title', self.strings.unavailableCombo);
      }

      /* Contextual prices, always from real variant prices. */
      var probe = values.slice();
      probe[index] = value;

      var withValue = self.prefixMin(probe, index + 1);
      var baseline = self.prefixMin(probe, index);

      var metaEl = btn.querySelector('[data-pcfg-value-meta]');
      if (metaEl) {
        var meta = [];
        var dims = self.dimensionsFor(index, value);
        if (dims) meta.push(dims);
        if (withValue !== null) {
          meta.push(self.strings.fromPrice + ' ' + formatMoney(withValue, fmt));
        }
        metaEl.textContent = meta.join(' · ');
      }

      var priceEl = btn.querySelector('[data-pcfg-value-price]');
      if (priceEl) {
        if (withValue === null || baseline === null) {
          priceEl.textContent = '';
        } else {
          var diff = withValue - baseline;
          priceEl.textContent = diff === 0
            ? self.strings.included
            : (diff > 0 ? '+' : '−') + formatMoney(Math.abs(diff), fmt);
        }
      }
    });
  };

  /** Dimensions metafield for a given option value, in current context. */
  Configurator.prototype.dimensionsFor = function (index, value) {
    var values = this.state.values;

    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (v.options[index] !== value) continue;

      var ok = true;
      for (var j = 0; j < index; j++) {
        if (v.options[j] !== values[j]) { ok = false; break; }
      }
      if (ok && v.dimensions) return v.dimensions;
    }
    return '';
  };

  /**
   * Re-orders the thumbnail strip so the current build's photos come first.
   *
   * Nothing is created, destroyed or moved in the DOM — each tile is only
   * given a CSS `order` and, past the configured limit, hidden. The
   * lazy-loaded <img> elements are therefore created once for the life of
   * the page, and a tile the shopper has already seen never reloads.
   */
  Configurator.prototype.renderThumbs = function () {
    var self = this;
    var group = this.bestGroup(this.state.values);

    // Media IDs belonging to the current build, in gallery order.
    var buildIds = group ? group.media.map(function (m) { return String(m.id); }) : [];
    var shown = 0;

    // Swatch-mirror tiles, when the setting is on, stay pinned to the front
    // and are repainted from the selected option button's own data-swatch-*
    // attributes — so the resolution chain in configurator-swatch.liquid
    // remains the only source of truth for what a swatch looks like.
    this.dom.thumbs.forEach(function (thumb) {
      var role = thumb.getAttribute('data-thumb-role');
      if (role !== 'option2' && role !== 'option3') return;

      var index = role === 'option2' ? 1 : 2;
      var value = self.state.values[index];
      var source = self.dom.optionButtons.filter(function (btn) {
        return parseInt(btn.getAttribute('data-option-index'), 10) === index &&
               btn.getAttribute('data-value') === value;
      })[0];

      var visual = thumb.querySelector('.pcfg__swatch-visual');
      if (visual && source) {
        var image = source.getAttribute('data-swatch-image');
        var color = source.getAttribute('data-swatch-color');
        visual.style.backgroundImage = image ? 'url(' + image + ')' : '';
        visual.style.backgroundColor = color || '';
      }

      var chip = thumb.querySelector('[data-pcfg-thumb-chip]');
      var names = self.data.optionNames || [];
      if (chip && value) chip.textContent = value;
      if (value) {
        thumb.setAttribute('aria-label', (names[index] ? names[index] + ': ' : '') + value);
      }

      thumb.style.order = '-1';
      thumb.hidden = false;
      shown++;
    });

    // Image tiles: build group first, then the rest of the gallery.
    this.dom.thumbs.forEach(function (thumb) {
      if (thumb.getAttribute('data-thumb-role') !== 'media') return;

      var id = thumb.getAttribute('data-media-id');
      var position = buildIds.indexOf(id);

      // Tiles in the build group get orders 0..n; everything else is pushed
      // behind them but keeps its original gallery sequence.
      thumb.style.order = position !== -1
        ? String(position)
        : String(1000 + self.media.findIndex(function (m) { return String(m.id) === id; }));
    });

    // Video tiles sit just after the current build's photos (orders 0..n) and
    // before unrelated photos (1000+). They always show and are never capped.
    this.dom.thumbs.forEach(function (thumb, i) {
      if (thumb.getAttribute('data-thumb-role') !== 'video') return;
      thumb.style.order = String(500 + i);
      thumb.hidden = false;
    });

    // Setting "Photos of other colors" = hide: a photo tagged (alt text or [key: value]) with a
    // DIFFERENT value of a swatch / finish-card step (fabric, leather, finish…) is hidden. Untagged
    // photos — lifestyle, details, dimensions — always stay. Size steps never hide anything.
    var hideIds = {};
    if (this.data.otherBuilds === 'hide') {
      var colorIdx = [];
      this.dom.optionButtons.forEach(function (b) {
        if (b.classList.contains('pcfg__value--swatch') || b.classList.contains('pcfg__value--card')) {
          var ix = parseInt(b.getAttribute('data-option-index'), 10);
          if (colorIdx.indexOf(ix) === -1) colorIdx.push(ix);
        }
      });
      var visibleLeft = 0;
      this.media.forEach(function (m) {
        var sig = self.signatureFor(m);
        var other = false;
        if (sig) {
          colorIdx.forEach(function (ix) {
            var want = self.state.values[ix];
            if (sig[ix] && want && sig[ix] !== handleize(want)) other = true;
          });
        }
        if (other) hideIds[String(m.id)] = true; else visibleLeft++;
      });
      if (!visibleLeft) hideIds = {}; // never empty the strip
    }

    // Apply the "thumbnails shown" limit against the NEW order, so the cap
    // never hides a photo of the build the shopper is actually looking at.
    var ordered = this.dom.thumbs
      .filter(function (t) { return t.getAttribute('data-thumb-role') === 'media'; })
      .slice()
      .sort(function (a, b) { return parseInt(a.style.order, 10) - parseInt(b.style.order, 10); });

    ordered.forEach(function (thumb) {
      if (hideIds[thumb.getAttribute('data-media-id')]) { thumb.hidden = true; return; }
      var over = self.maxThumbs > 0 && shown >= self.maxThumbs;
      thumb.hidden = over;
      if (!over) shown++;
    });

    // The 3D tile, if there is one, always sits last.
    this.dom.thumbs.forEach(function (thumb) {
      if (thumb.getAttribute('data-thumb-role') === 'model') thumb.style.order = '9999';
    });

    this.updateThumbArrows();
  };

  /** Marks which tile is currently in the stage. */
  Configurator.prototype.markActiveThumb = function (mediaId) {
    this.dom.thumbs.forEach(function (t) {
      var on = mediaId != null && t.getAttribute('data-media-id') === String(mediaId);
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  Configurator.prototype.activateThumb = function (thumb) {
    var self = this;

    this.dom.thumbs.forEach(function (t) {
      var on = t === thumb;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    // Video tile: play the matching clip INLINE in the stage instead of
    // swapping the photo. setMainImage (option changes, image tiles) clears it.
    if (thumb.getAttribute('data-thumb-role') === 'video') {
      this.showStageVideo(thumb.getAttribute('data-media-id'));
      return;
    }

    var src = thumb.getAttribute('data-thumb-src');

    if (src) {
      this.setMainImage({ src: src, alt: thumb.getAttribute('data-thumb-alt') || '' });
    } else {
      // Swatch tiles have no photo of their own; showing the best render
      // for the current build is the useful thing to do.
      this.setMainImage(this.findImage(self.currentVariant));
    }
  };

  /* ---- Inline video in the stage ---- *
   * Shopify-hosted product videos play where the photo is, not in a lightbox.
   * Each video has its own hidden <video> in the stage (configurator-gallery);
   * we reveal the chosen one, hide the photo, and autoplay it muted + looping
   * (user-initiated via the thumbnail click, so the play() is allowed). The
   * controls let the shopper unmute or scrub. */
  Configurator.prototype.showStageVideo = function (mediaId) {
    var stage = this.dom.stage;
    if (!stage) return;
    var playing = null;
    var wraps = stage.querySelectorAll('[data-pcfg-stage-video]');
    Array.prototype.forEach.call(wraps, function (wrap) {
      var match = wrap.getAttribute('data-media-id') === String(mediaId);
      wrap.hidden = !match;
      var v = wrap.querySelector('video');
      if (v && !match) { try { v.pause(); } catch (e) {} }
      if (match) playing = v;
    });
    stage.classList.add('is-video');
    if (playing) {
      try { playing.muted = true; var p = playing.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
    }
  };

  Configurator.prototype.hideStageVideo = function () {
    var stage = this.dom.stage;
    if (!stage || !stage.classList.contains('is-video')) return;
    var wraps = stage.querySelectorAll('[data-pcfg-stage-video]');
    Array.prototype.forEach.call(wraps, function (wrap) {
      wrap.hidden = true;
      var v = wrap.querySelector('video');
      if (v) { try { v.pause(); } catch (e) {} }
    });
    stage.classList.remove('is-video');
  };

  /* ---- Swipe the main image (touch) ---- */

  /**
   * Photo tiles in the order the shopper currently sees them. The strip is
   * re-sorted with the CSS `order` property as the build changes (see
   * renderThumbs), so DOM order alone would swipe to the wrong photo.
   */
  Configurator.prototype.orderedPhotoThumbs = function () {
    return this.dom.thumbs
      .map(function (t, i) { return { t: t, i: i, o: parseInt(t.style.order || '0', 10) || 0 }; })
      .filter(function (x) {
        var role = x.t.getAttribute('data-thumb-role');
        return (role === 'media' || role === 'model') && !x.t.hidden && x.t.offsetParent !== null;
      })
      .sort(function (a, b) { return a.o - b.o || a.i - b.i; })
      .map(function (x) { return x.t; });
  };

  Configurator.prototype.stepImage = function (dir) {
    var list = this.orderedPhotoThumbs();
    if (list.length < 2) return;
    var here = -1;
    for (var i = 0; i < list.length; i++) {
      if (list[i].classList.contains('is-active')) { here = i; break; }
    }
    var next = list[(here + dir + list.length) % list.length];
    this.activateThumb(next);

    // Keep the strip in step so the highlighted tile stays in view.
    var strip = this.dom.thumbStrip;
    if (strip) {
      strip.scrollTo({ left: next.offsetLeft - (strip.clientWidth - next.clientWidth) / 2, behavior: 'smooth' });
    }
  };

  Configurator.prototype.updateStageCount = function () {
    if (!this.dom.stageCount) return;
    var list = this.orderedPhotoThumbs();
    var here = 0;
    for (var i = 0; i < list.length; i++) {
      if (list[i].classList.contains('is-active')) { here = i + 1; break; }
    }
    this.dom.stageCount.hidden = list.length < 2 || here === 0;
    if (this.dom.stageArrows) {
      var few = list.length < 2;
      this.dom.stageArrows.forEach(function (b) { b.hidden = few; });
    }
    this.dom.stageCount.textContent = here + ' / ' + list.length;
  };

  Configurator.prototype.initStageSwipe = function () {
    var self = this;
    var stage = this.root.querySelector('.pcfg__stage');
    if (!stage) return;

    // "2 / 6" counter — the visible hint that the photo can be swiped (shown on phones only, via CSS).
    var count = document.createElement('span');
    count.className = 'pcfg__stage-count';
    count.setAttribute('aria-hidden', 'true');
    count.hidden = true;
    stage.appendChild(count);
    this.dom.stageCount = count;

    // Prev / next arrows on the photo (desktop; same order as swipe and the thumbnail strip).
    var mode = this.data.stageArrows || 'always';
    if (mode !== 'off') {
      stage.classList.add('pcfg__stage--arrows-' + mode);
      var chevron = function (d) {
        return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' + d + '" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
      };
      this.dom.stageArrows = [
        { dir: -1, cls: 'prev', label: 'Previous photo', d: 'M15 4 7 12l8 8' },
        { dir: 1, cls: 'next', label: 'Next photo', d: 'M9 4l8 8-8 8' }
      ].map(function (a) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'pcfg__stage-arrow pcfg__stage-arrow--' + a.cls;
        b.setAttribute('aria-label', a.label);
        b.innerHTML = chevron(a.d);
        b.hidden = true;
        b.addEventListener('click', function () { self.stepImage(a.dir); });
        stage.appendChild(b);
        return b;
      });
    }

    var x0 = null, y0 = 0, t0 = 0;
    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { x0 = null; return; }
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; t0 = Date.now();
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var p = e.changedTouches[0];
      var dx = p.clientX - x0, dy = p.clientY - y0;
      x0 = null;
      // A deliberate sideways flick: far enough, clearly more sideways than vertical, not a slow drag.
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3 || Date.now() - t0 > 800) return;
      self.stepImage(dx < 0 ? 1 : -1);
    }, { passive: true });

    // Refresh the counter whenever any tile becomes active (thumb tap, swipe, option change).
    var obs = new MutationObserver(function () { self.updateStageCount(); });
    this.dom.thumbs.forEach(function (t) { obs.observe(t, { attributes: true, attributeFilter: ['class', 'style'] }); });
    this.updateStageCount();
  };

  /* ---- Slider arrows ---- */

  Configurator.prototype.initThumbArrows = function () {
    var self = this;
    var strip = this.dom.thumbStrip;
    if (!strip) return;

    function page(direction) {
      // Scroll by just under a full strip width so one tile stays visible
      // as an anchor, which is what makes paging feel continuous.
      strip.scrollBy({ left: direction * strip.clientWidth * 0.8, behavior: 'smooth' });
    }

    if (this.dom.thumbPrev) {
      this.dom.thumbPrev.addEventListener('click', function () { page(-1); });
    }
    if (this.dom.thumbNext) {
      this.dom.thumbNext.addEventListener('click', function () { page(1); });
    }

    strip.addEventListener('scroll', function () {
      self.updateThumbArrows();
    }, { passive: true });

    window.addEventListener('resize', function () {
      self.updateThumbArrows();
    });
  };

  /** Arrows appear only when there is somewhere to scroll to. */
  Configurator.prototype.updateThumbArrows = function () {
    var strip = this.dom.thumbStrip;
    if (!strip) return;

    var overflowing = strip.scrollWidth - strip.clientWidth > 2;

    if (this.dom.thumbPrev) {
      this.dom.thumbPrev.hidden = !overflowing || strip.scrollLeft <= 2;
    }
    if (this.dom.thumbNext) {
      this.dom.thumbNext.hidden =
        !overflowing || strip.scrollLeft >= strip.scrollWidth - strip.clientWidth - 2;
    }
  };

  Configurator.prototype.renderBuild = function () {
    var self = this;
    var fmt = this.data.moneyFormat;
    var values = this.state.values;
    var variant = this.currentVariant;

    this.dom.buildRows.forEach(function (row) {
      var index = parseInt(row.getAttribute('data-option-index'), 10);

      var valueEl = row.querySelector('[data-pcfg-build-value]');
      if (valueEl) {
        var label = values[index] || '';
        // The size row carries its dimensions, as in "Sofa · 84" W".
        if (index === 0 && variant && variant.dimensions) label += ' · ' + variant.dimensions;
        valueEl.textContent = label;
      }

      var priceEl = row.querySelector('[data-pcfg-build-price]');
      if (!priceEl) return;

      var upper = self.prefixMin(values, index + 1);
      var lower = index === 0 ? 0 : self.prefixMin(values, index);

      if (upper === null || lower === null) {
        priceEl.textContent = '';
        return;
      }

      var effect = upper - lower;

      // Step 1 shows the absolute base price; later steps show the delta
      // they add, so the three rows sum exactly to the total below.
      if (index === 0) {
        priceEl.textContent = formatMoney(effect, fmt);
      } else {
        priceEl.textContent = effect === 0
          ? self.strings.included
          : (effect > 0 ? '+' : '−') + formatMoney(Math.abs(effect), fmt);
      }
    });

    if (this.dom.buildTotal && variant) {
      this.dom.buildTotal.textContent = formatMoney(variant.price, fmt);
    }
  };

  /** Debounced so arrowing through options produces one announcement. */
  Configurator.prototype.announce = function (priceText) {
    var el = this.dom.announce;
    if (!el) return;

    var template = this.strings.priceAnnounce || 'Price updated to [price]';

    window.clearTimeout(this._announceTimer);
    this._announceTimer = window.setTimeout(function () {
      el.textContent = template.replace('[price]', priceText);
    }, 600);
  };

  /* ------------------------------------------------------------------ *
   * Add to cart — reusing Impulse's handler
   * ------------------------------------------------------------------ */

  Configurator.prototype.initAddToCart = function () {
    var self = this;
    var form = this.dom.form;
    if (!form) return;

    /* theme.js is loaded with `defer` from the layout and this file is
       deferred from the section, so theme.js normally executes first. The
       short poll covers the case where it has not, rather than silently
       falling back to a bespoke /cart/add.js flow that would bypass the
       cart drawer. */
    var tries = 0;

    (function attach() {
      if (window.theme && theme.AjaxProduct) {
        new theme.AjaxProduct(form, '[data-pcfg-atc]');
        return;
      }

      if (++tries < 40) {
        window.setTimeout(attach, 50);
      } else {
        console.warn('[configurator] theme.AjaxProduct unavailable; the form will submit normally');
      }
    })();

    // The sticky bar never submits on its own — it drives the real form, so
    // there is exactly one add-to-cart path and one loading state.
    if (this.dom.stickyAtc) {
      this.dom.stickyAtc.addEventListener('click', function () {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(self.dom.atc);
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }
  };

  /* ------------------------------------------------------------------ *
   * Sticky mobile bar
   * ------------------------------------------------------------------ */

  /**
   * Decides whether the sticky bar should be showing, given one
   * IntersectionObserver entry for the real Add to cart button.
   *
   * "Not intersecting" on its own is NOT the right test. On a phone the
   * button starts far below the fold, so it is already not intersecting at
   * page load — using that alone would pop the sticky bar up immediately,
   * before the shopper has ever seen the real button. The bar should only
   * appear once the button has been scrolled PAST, which is what the
   * negative `top` adds: the button's box is above the viewport.
   *
   * Exposed on the prototype rather than inlined so the rule can be tested
   * directly without having to drive a real scroll.
   */
  Configurator.prototype.shouldShowSticky = function (entry) {
    return !entry.isIntersecting && entry.boundingClientRect.top < 0;
  };

  Configurator.prototype.initStickyBar = function () {
    var self = this;
    var bar = this.dom.sticky;
    var anchor = this.dom.atc;

    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var show = self.shouldShowSticky(entry);
        bar.hidden = !show;
        bar.classList.toggle('is-visible', show);
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0 });

    observer.observe(anchor);
  };

  /* ------------------------------------------------------------------ *
   * Free swatch box
   * ------------------------------------------------------------------ */

  Configurator.prototype.storageKey = function () {
    return 'seldens:swatch-box';
  };

  /** Every storage access is wrapped: Safari private mode throws on write,
   *  and a corrupt value must not break the configurator. */
  Configurator.prototype.readSwatches = function () {
    try {
      var raw = window.localStorage.getItem(this.storageKey());
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  Configurator.prototype.writeSwatches = function (list) {
    try {
      window.localStorage.setItem(this.storageKey(), JSON.stringify(list));
    } catch (e) {
      /* Storage unavailable. The label still updates for this page view. */
    }
  };

  Configurator.prototype.initSwatchBox = function () {
    var self = this;
    var box = this.dom.swatchBox;
    if (!box) return;

    this.swatchDom = {
      box: box,
      add: box.querySelector('[data-pcfg-swatch-add]'),
      addText: box.querySelector('[data-pcfg-swatch-add-text]'),
      count: box.querySelector('[data-pcfg-swatch-count]'),
      dots: box.querySelector('[data-pcfg-swatch-dots]'),
      request: box.querySelector('[data-pcfg-swatch-request]'),
      panel: box.querySelector('[data-pcfg-swatch-panel]'),
      form: box.querySelector('.pcfg__swatch-form'),
      swatchField: box.querySelector('[data-pcfg-swatch-field]'),
      buildField: box.querySelector('[data-pcfg-build-field]'),
      buildUrlField: box.querySelector('[data-pcfg-build-url-field]'),
      bodyField: box.querySelector('[data-pcfg-body-field]')
    };

    this.swatchMax = parseInt(box.getAttribute('data-max'), 10) || 5;
    this.swatches = this.readSwatches();

    if (this.swatchDom.add) {
      this.swatchDom.add.addEventListener('click', function () {
        self.addSwatch();
      });
    }

    if (this.swatchDom.request && this.swatchDom.panel) {
      this.swatchDom.request.addEventListener('click', function () {
        var open = self.swatchDom.panel.hidden;
        self.swatchDom.panel.hidden = !open;
        self.swatchDom.request.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    // Fill the hidden fields at submit time — the list only exists here.
    if (this.swatchDom.form) {
      this.swatchDom.form.addEventListener('submit', function () {
        self.fillSwatchFields();
      });
    }

    // A successful submission re-renders the page with the panel closed by
    // default, so re-open it to show the confirmation message.
    if (this.swatchDom.panel && this.swatchDom.panel.querySelector('.pcfg__swatch-success')) {
      this.swatchDom.panel.hidden = false;
      if (this.swatchDom.request) this.swatchDom.request.setAttribute('aria-expanded', 'true');
    }

    this.renderSwatchBox();
  };

  /** The Step 2 value is what a swatch represents (fabric / leather). */
  Configurator.prototype.currentSwatchValue = function () {
    return this.state.values[1] || this.state.values[0] || '';
  };

  Configurator.prototype.addSwatch = function () {
    var value = this.currentSwatchValue();
    if (!value) return;

    if (this.swatches.indexOf(value) !== -1) return;   // no duplicates
    if (this.swatches.length >= this.swatchMax) return; // box is full

    this.swatches.push(value);
    this.writeSwatches(this.swatches);
    this.renderSwatchBox();
  };

  Configurator.prototype.renderSwatchBox = function () {
    if (!this.swatchDom) return;

    var d = this.swatchDom;
    var value = this.currentSwatchValue();
    var saved = this.swatches.indexOf(value) !== -1;
    var full = this.swatches.length >= this.swatchMax;

    if (d.addText) {
      var label;
      if (saved) {
        label = (this.strings.swatchSaved || '').replace('[value]', value);
      } else if (full) {
        label = this.strings.swatchFull || '';
      } else {
        label = (this.strings.swatchAdd || '').replace('[value]', value);
      }
      d.addText.textContent = label;
    }

    if (d.add) d.add.disabled = saved || full;
    if (d.count) d.count.textContent = String(this.swatches.length);

    if (d.dots) {
      var html = '';
      for (var i = 0; i < this.swatchMax; i++) {
        html += '<span class="pcfg__swatch-dot' + (i < this.swatches.length ? ' is-filled' : '') + '"></span>';
      }
      d.dots.innerHTML = html;
    }

    if (d.request) d.request.hidden = this.swatches.length === 0;
  };

  Configurator.prototype.fillSwatchFields = function () {
    var d = this.swatchDom;
    var values = this.state.values.filter(Boolean);
    var names = this.data.optionNames || [];

    var build = values.map(function (v, i) {
      return (names[i] ? names[i] + ': ' : '') + v;
    }).join(' / ');

    var link = window.location.href;

    if (d.swatchField) d.swatchField.value = this.swatches.join(', ');
    if (d.buildField) d.buildField.value = build;
    if (d.buildUrlField) d.buildUrlField.value = link;

    if (d.bodyField) {
      d.bodyField.value =
        'Free swatch request from the product configurator.\n\n' +
        'Product: ' + this.data.productTitle + '\n' +
        'Build: ' + build + '\n' +
        'Swatches requested: ' + this.swatches.join(', ') + '\n' +
        'Build link: ' + link;
    }
  };

  /* ------------------------------------------------------------------ *
   * Blocks ported from the live seldens-pdp-v1-1 template
   * ------------------------------------------------------------------ */

  Configurator.prototype.initPortedBlocks = function () {
    this.initSkuRow();
    this.initDeliveryRow();
    this.initDetailsLink();
    this.initSaleAlert();
  };

  /**
   * Info rows: the in-stock label and the special-order delivery widget
   * occupy the same row and are mutually exclusive.
   *
   * The widget hides itself in Liquid for stocked items, so when it IS
   * showing the item is made to order and "In Stock" beside "Delivered in
   * 12-16 weeks" would contradict it. Decided here rather than in Liquid
   * because the widget also flips itself per variant after load.
   */
  Configurator.prototype.initDeliveryRow = function () {
    var label = this.root.querySelector('[data-pcfg-instock]');
    var widget = this.root.querySelector('.pcfg__inforow .special-order-widget');

    if (!label || !widget) return;

    function sync() {
      var visible = window.getComputedStyle(widget).display !== 'none';
      label.hidden = visible;
    }

    sync();

    // The widget toggles its own inline display as the variant changes.
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(sync);
      observer.observe(widget, { attributes: true, attributeFilter: ['style'] });
    }
  };

  /**
   * Info rows: keep the SKU in step with the selected variant.
   *
   * The map is read from a JSON block rather than a global built by a
   * Liquid loop, so it cannot be half-overwritten by anything else on the
   * page.
   */
  Configurator.prototype.initSkuRow = function () {
    var mapEl = this.root.querySelector('[data-pcfg-sku-map]');
    this.skuEls = Array.prototype.slice.call(this.root.querySelectorAll('[data-pcfg-sku]'));
    this.skuEl = this.skuEls[0];

    if (!mapEl || !this.skuEl) return;

    try {
      this.skuMap = JSON.parse(mapEl.textContent);
    } catch (e) {
      this.skuMap = null;
    }

    this.renderSku();
  };

  Configurator.prototype.renderSku = function () {
    if (!this.skuMap || !this.skuEl || !this.currentVariant) return;

    // Storis ID first; fall back to the variant's own SKU rather than blanking the row.
    var sku = this.skuMap[String(this.currentVariant.id)] || this.currentVariant.sku || '';

    this.skuEls.forEach(function (el) {
      el.textContent = sku;
      var skuRow = el.closest('.pcfg__inforow') || el.closest('.pcfg__sc-row');
      if (skuRow) skuRow.hidden = !sku;
    });
  };

  /**
   * Info rows: the "View product details" button scrolls to the details
   * accordion further down the page. The selectors below are tried in
   * order so this keeps working if the section is renamed or reordered.
   */
  Configurator.prototype.initDetailsLink = function () {
    var btn = this.root.querySelector('[data-pcfg-scroll-details]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var target =
        document.getElementById('shopify-section-pdp-details-accordion') ||
        document.querySelector('.pdp-details-accordion') ||
        document.querySelector('[id^="shopify-section-pdp-details"]') ||
        document.querySelector('[id^="shopify-section-pdp-why-love-it"]');

      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Open the first accordion panel if it is a <details>, so the click
      // lands on content rather than on a row the shopper must open.
      var firstPanel = target.querySelector('details');
      if (firstPanel && !firstPanel.open) firstPanel.open = true;
    });
  };

  /**
   * Sale alert: subscribe to the Klaviyo list.
   *
   * Same client-subscriptions endpoint and revision as the live v1-1
   * block, so an existing Klaviyo list keeps working untouched. Bound from
   * here rather than from an inline <script> in the snippet, because an
   * inline script re-runs and re-binds every time the theme editor
   * re-renders the section.
   */
  Configurator.prototype.initSaleAlert = function () {
    var block = this.root.querySelector('[data-pcfg-sale-alert]');
    if (!block) return;

    var form = block.querySelector('[data-pcfg-sale-alert-form]');
    var done = block.querySelector('[data-pcfg-sale-alert-done]');
    var errorEl = block.querySelector('[data-pcfg-sale-alert-error]');
    if (!form) return;

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      if (errorEl) errorEl.hidden = true;
    }

    function finish() {
      form.hidden = true;

      var fine = block.querySelector('.pcfg__sa-fine');
      var watch = block.querySelector('.pcfg__sa-watch');
      if (fine) fine.hidden = true;
      if (watch) watch.hidden = true;
      if (done) done.hidden = false;
    }

    form.addEventListener('input', clearError);

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();

      var email = (form.email && form.email.value || '').trim();
      var phone = (form.phone && form.phone.value || '').trim();

      // Validate before doing anything: an empty submit should say so
      // inline, not silently do nothing.
      if (!email || email.indexOf('@') === -1) {
        showError('Enter a valid email address.');
        if (form.email) form.email.focus();
        return;
      }

      clearError();

      var btn = form.querySelector('button');
      if (btn) btn.disabled = true;

      var d = block.dataset;

      // Klaviyo onsite tracking, when the script happens to be present.
      try {
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', { $email: email, $phone_number: phone }]);
        window._learnq.push(['track', 'Requested Sale Alert', {
          ProductName: d.product,
          ProductID: d.pid,
          Handle: d.handle,
          Vendor: d.vendor,
          ProductType: d.ptype,
          Collection: d.collection,
          Price: d.price,
          URL: d.url
        }]);
      } catch (e) { /* tracking is best effort */ }

      if (!d.company || !d.list) {
        // Not configured yet: still confirm to the shopper rather than
        // leaving a dead button, and make the gap obvious in the console.
        console.warn('[configurator] sale alert has no Klaviyo company id or list id set');
        finish();
        return;
      }

      var attributes = {
        email: email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };

      if (phone) {
        attributes.phone_number = phone;
        attributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }

      var payload = {
        data: {
          type: 'subscription',
          attributes: { profile: { data: { type: 'profile', attributes: attributes } } },
          relationships: { list: { data: { type: 'list', id: d.list } } }
        }
      };

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(d.company), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload)
      }).then(finish).catch(finish);
    });
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    var roots = document.querySelectorAll('[data-section-type="product-configurator"]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.pcfgReady === 'true') return;
      root.dataset.pcfgReady = 'true';
      // Kept on the element so the instance can be inspected from the
      // console when debugging a build on a real device.
      root.pcfg = new Configurator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (evt) {
    if (evt.target && evt.target.querySelector('[data-section-type="product-configurator"]')) {
      init();
    }
  });
})();
